import { Component, OnInit } from '@angular/core';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatButtonModule } from '@angular/material/button';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { RouterLink } from '@angular/router';
import { Order } from '../../models/Order';
import { GatewayOrder } from '../../models/GatewayOrder';
import { Store } from '@ngrx/store';
import { OrderState } from '../../state/order/order.reducer';
import { AsyncPipe, CommonModule } from '@angular/common';
import { map, Observable, combineLatest } from 'rxjs';
import {
  selectGetOrders,
  selectGetReflexOrders,
} from '../../state/order/order.selector';
import {
  getOrdersByMe,
  getReflexOrders,
} from '../../state/order/order.actions';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-orders',
  imports: [
    MatTableModule,
    MatSortModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatSelectModule,
    MatButtonToggleModule,
    MatTabsModule,
    RouterLink,
    AsyncPipe,
    MatIconModule,
    MatCardModule,
    CommonModule,
    FormsModule,
    MatTooltipModule,
    PageHeaderComponent
],
  templateUrl: './orders.component.html',
  styleUrl: './orders.component.css',
})
export class OrdersComponent implements OnInit {
  gatewayOrders$: Observable<GatewayOrder[]>;
  currentOrders$: Observable<GatewayOrder[]> = new Observable<GatewayOrder[]>();
  completedOrders$: Observable<GatewayOrder[]> = new Observable<GatewayOrder[]>();
  orders$: Observable<Order[]>;
  ordersMap: Map<string, Order> = new Map();
  statuses$: Observable<string[]>;
  availableYears: number[] = [];
  selectedYear: number = new Date().getFullYear();
  quickRange:
    | 'today'
    | 'last7'
    | 'thisMonth'
    | 'thisMonthFull'
    | 'last30'
    | 'none' = 'none';
  isHandset: boolean = false;
  // Filters (Material datepickers expect Date|null)
  filterFromDate: Date | null = null;
  filterToDate: Date | null = null;
  filterStatus: string = '';
  constructor(
    private store: Store<{ order: OrderState }>,
    private breakpointObserver: BreakpointObserver
  ) {
    this.gatewayOrders$ = this.store.select(selectGetReflexOrders);

    // Get all orders without filtering to build the complete map
    this.orders$ = this.store.select(selectGetOrders);

    // Subscribe to orders to build the map with ALL orders
    this.orders$.subscribe(orders => {
      this.ordersMap.clear();
      (orders || []).forEach(order => {
        if (order.reflexOrderNo) {
          // Convert to string to ensure consistent Map keys
          const key = String(order.reflexOrderNo);
          this.ordersMap.set(key, order);
        }
      });
    });

    // this.gatewayOrders$ = this.store.select(selectGetReflexOrders).pipe(
    //   map((orders) => {
    //     const currentYear = new Date().getFullYear();
    //     const needsYearRestriction = !this.hasActiveFilters();
    //     return (orders || [])
    //       .filter((order) => {
    //         if (!order.dateFrom) return false;
    //         if (!needsYearRestriction) return true;
    //         return new Date(order.dateFrom).getFullYear() >= currentYear;
    //       })
    //       .map((order) => Order.parseGatewayOrder(order));
    //   })
    // );
    // this.orders$ = this.store.select(selectGetOrders).pipe(
    //   map(orders => {
    //     const currentYear = new Date().getFullYear();
    //     return (orders || []).filter(order => {
    //       const date = order.createdDate ? new Date(order.createdDate) : null;
    //       return date && date.getFullYear() === currentYear;
    //     });
    //   })
    // );
    this.statuses$ = this.gatewayOrders$.pipe(
      map((orders) =>
        Array.from(
          new Set(
            (orders || [])
              .map((o) => (o.statusText || '').trim())
              .filter((name) => !!name)
          )
        ).sort((a, b) => a.localeCompare(b, 'de'))
      )
    );
    this.observeHandset();
    this.setupOrderStreams();
  }

  private setupOrderStreams(): void {
    const currentYear = new Date().getFullYear();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Current orders: includes upcoming and active orders (today <= dateTo)
    // This merges the previous "upcoming" and "current" tabs
    this.currentOrders$ = this.gatewayOrders$.pipe(
      map((orders) => {
        return (orders || []).filter((order) => {
          if (!order.dateTo) return false;

          const dateTo = new Date(order.dateTo);
          dateTo.setHours(23, 59, 59, 999); // Include the entire end date

          // An order is "current" if it hasn't ended yet (includes upcoming and active)
          return today <= dateTo;
        }).sort((a, b) => {
          // Sort by dateFrom ascending (soonest first)
          const dateFromA = a.dateFrom ? new Date(a.dateFrom).getTime() : 0;
          const dateFromB = b.dateFrom ? new Date(b.dateFrom).getTime() : 0;
          return dateFromA - dateFromB;
        });
      })
    );

    // Completed orders: current date > dateTo (order end date has passed)
    this.completedOrders$ = this.gatewayOrders$.pipe(
      map((orders) => {
        const allCompleted = (orders || []).filter((order) => {
          if (!order.dateTo) return false;

          const dateTo = new Date(order.dateTo);
          dateTo.setHours(23, 59, 59, 999); // Include the entire end date

          // Order is completed if today is after the end date
          return today > dateTo;
        });

        // Extract available years
        const years = new Set<number>();
        allCompleted.forEach(order => {
          if (order.dateFrom) {
            years.add(new Date(order.dateFrom).getFullYear());
          }
        });
        this.availableYears = Array.from(years).sort((a, b) => b - a);
        if (this.availableYears.length > 0 && !this.selectedYear) {
          this.selectedYear = this.availableYears[0];
        }

        // Filter by selected year
        return allCompleted.filter(order => {
          if (!order.dateFrom) return false;
          return new Date(order.dateFrom).getFullYear() === this.selectedYear;
        }).sort((a, b) => {
          // Primary sort: dateFrom descending (most future first)
          const dateFromA = a.dateFrom ? new Date(a.dateFrom).getTime() : 0;
          const dateFromB = b.dateFrom ? new Date(b.dateFrom).getTime() : 0;
          if (dateFromB !== dateFromA) {
            return dateFromB - dateFromA;
          }
          // Secondary sort: dateTo descending (most future first)
          const dateToA = a.dateTo ? new Date(a.dateTo).getTime() : 0;
          const dateToB = b.dateTo ? new Date(b.dateTo).getTime() : 0;
          return dateToB - dateToA;
        });
      })
    );
  }

  onYearChange(year: number): void {
    this.selectedYear = year;
    this.setupOrderStreams();
  }

  private observeHandset(): void {
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .subscribe((result) => {
        this.isHandset = result.matches;
      });
  }

  ngOnInit(): void {
    const loaded = this.loadFiltersFromSession();
    if (loaded) {
      this.applyFilters();
    } else {
      this.store.dispatch(getReflexOrders({}));
      this.store.dispatch(getOrdersByMe());
    }
  }

  applyFilters(): void {
    const filters: any = {};
    if (this.filterFromDate instanceof Date) {
      filters.fromDate = this.filterFromDate.toISOString();
    }
    if (this.filterToDate instanceof Date) {
      filters.toDate = this.filterToDate.toISOString();
    }
    if (this.filterStatus) {
      filters.status = this.filterStatus;
    }
    this.saveFiltersToSession();
    this.store.dispatch(getReflexOrders({ filters }));
  }

  resetFilters(): void {
    this.filterFromDate = null;
    this.filterToDate = null;
    this.filterStatus = '';
    this.quickRange = 'none';
    this.saveFiltersToSession();
    this.store.dispatch(getReflexOrders({}));
  }

  onQuickRangeChange(
    value: 'today' | 'last7' | 'thisMonth' | 'thisMonthFull' | 'last30' | 'none'
  ): void {
    this.quickRange = value ?? 'none';
    switch (this.quickRange) {
      case 'today':
        this.setRange(this.startOfToday(), this.endOfToday());
        break;
      case 'last7':
        this.setRange(this.addDays(this.startOfToday(), -6), this.endOfToday());
        break;
      case 'thisMonth':
        this.setRange(this.startOfMonth(new Date()), this.endOfToday());
        break;
      case 'thisMonthFull':
        this.setRange(
          this.startOfMonth(new Date()),
          this.endOfMonth(new Date())
        );
        break;
      case 'last30':
        this.setRange(
          this.addDays(this.startOfToday(), -29),
          this.endOfToday()
        );
        break;
      default:
        this.filterFromDate = null;
        this.filterToDate = null;
        break;
    }
    // do not immediately filter, let the user use the apply filter button
    // this.applyFilters();
  }

  private setRange(from: Date, to: Date): void {
    this.filterFromDate = from;
    this.filterToDate = to;
  }

  private startOfToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfToday(): Date {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private startOfMonth(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private endOfMonth(date: Date): Date {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    d.setHours(23, 59, 59, 999);
    return d;
  }

  private addDays(date: Date, days: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  getCoinsUsed(gatewayOrderNo: string): number {
    // Ensure the key is a string for consistent Map lookup
    const key = String(gatewayOrderNo);
    const order = this.ordersMap.get(key);
    return order?.coinsUsed ?? 0;
  }
  private hasActiveFilters(): boolean {
    return (
      this.filterFromDate instanceof Date ||
      this.filterToDate instanceof Date ||
      !!this.filterStatus ||
      this.quickRange !== 'none'
    );
  }

  //TODO: refactor to use state management
  private saveFiltersToSession(): void {
    try {
      const payload = {
        fromDate: this.filterFromDate
          ? this.filterFromDate.toISOString()
          : null,
        toDate: this.filterToDate ? this.filterToDate.toISOString() : null,
        status: this.filterStatus || '',
        quickRange: this.quickRange || 'none',
      };
      sessionStorage.setItem('orders.filters', JSON.stringify(payload));
    } catch { }
  }

  //TODO: refactor to use state management
  private loadFiltersFromSession(): boolean {
    try {
      const raw = sessionStorage.getItem('orders.filters');
      if (!raw) return false;
      const saved = JSON.parse(raw) as {
        fromDate: string | null;
        toDate: string | null;
        status: string;
        quickRange: string;
      };
      this.filterFromDate = saved.fromDate ? new Date(saved.fromDate) : null;
      this.filterToDate = saved.toDate ? new Date(saved.toDate) : null;
      this.filterStatus = saved.status || '';
      const qr = saved.quickRange as any as
        | 'today'
        | 'last7'
        | 'thisMonth'
        | 'thisMonthFull'
        | 'last30'
        | 'none';
      this.quickRange = qr || 'none';
      return !!(this.filterFromDate || this.filterToDate || this.filterStatus);
    } catch {
      return false;
    }
  }
}
